import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_MAP_ID, getMapOrDefault, MAPS, REGIONS } from "@/data/maps.ts";
import { FACTIONS, getFaction, type FactionId } from "@/data/factions.ts";
import type { RoleId } from "@/data/roles.ts";
import type { Poi } from "@/data/pois.ts";
import { markerUrl } from "@/data/markers.ts";
import { squadTag, UNASSIGNED_SQUAD } from "@/data/squads.ts";
import { gridConfigFor } from "@/data/maps.ts";
import { MapContextMenu } from "@/features/map/MapContextMenu.tsx";
import { MarkerPalette, type PendingMarker } from "@/features/map/MarkerPalette.tsx";
import { PlaceSearch } from "@/features/map/PlaceSearch.tsx";
import { MapView, type MapApi } from "@/features/map/MapView.tsx";
import type { OrderMarker } from "@/features/map/OrderLayer.tsx";
import type { GameCoord } from "@/engine/coords.ts";
import { CommandBar } from "@/features/session/CommandBar.tsx";
import { JoinScreen } from "@/features/session/JoinScreen.tsx";
import { Footer } from "@/features/session/Footer.tsx";
import { OrderPanel } from "@/features/session/OrderPanel.tsx";
import { ShortcutsHelp } from "@/features/session/ShortcutsHelp.tsx";
import { SquadRoster } from "@/features/session/SquadRoster.tsx";
import { loadIdentity, saveIdentity, type Identity } from "@/session/identity.ts";
import { formatRoomCode, isValidRoomCode, normalizeRoomCode } from "@/session/roomCode.ts";
import { useSession } from "@/session/useSession.ts";

/**
 * Application shell.
 *
 * Two screens: join, then the session. The session layout is built around one
 * rule — the map never shrinks to make room for chrome. Command sits in a thin
 * bar above it, the roster in a narrow column beside it, and everything else
 * is a keyboard shortcut.
 *
 * The relay URL is read from the environment. With one configured the session
 * uses a WebSocket star topology, which carries a full 33-player team; without
 * one it falls back to a peer-to-peer mesh, which is fine for a squad.
 */
const WEBSOCKET_URL = import.meta.env["VITE_SYNC_URL"] as string | undefined;

export function App(): React.ReactElement {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [showLabels, setShowLabels] = useState(true);

  const stored = useMemo(() => loadIdentity(), []);

  /**
   * Rejoin automatically when the URL already names a session and this device
   * has been here before. Someone whose game crashes mid-match reopens the tab
   * and is straight back in — asking them to retype a code while their team is
   * mid-push is exactly the wrong moment for a form.
   *
   * A first-time visitor still gets the form, with the code filled in.
   */
  useEffect(() => {
    if (code) return;
    const fromUrl = normalizeRoomCode(window.location.pathname);
    if (!isValidRoomCode(fromUrl) || !stored.callsign) return;

    setIdentity({
      id: stored.id,
      callsign: stored.callsign,
      faction: stored.faction,
      role: stored.role,
    });
    setCode(fromUrl);
  }, [code, stored]);

  const handleStart = useCallback(
    (details: { code: string; callsign: string; faction: FactionId; role: RoleId }) => {
      const next: Identity = {
        id: stored.id,
        callsign: details.callsign,
        faction: details.faction,
        role: details.role,
      };
      saveIdentity({ ...next });
      setIdentity(next);
      setCode(details.code);
      // Keep the address bar in step so the tab can be shared or reloaded.
      window.history.replaceState(null, "", `/join/${details.code}`);
    },
    [stored.id],
  );

  if (!identity || !code) {
    return (
      <JoinScreen
        initialCallsign={stored.callsign}
        initialFaction={stored.faction}
        initialRole={stored.role}
        onStart={handleStart}
      />
    );
  }

  return (
    <SessionScreen
      code={code}
      identity={identity}
      onIdentityChange={(next) => {
        saveIdentity({ ...next });
        setIdentity(next);
      }}
      showGrid={showGrid}
      onToggleGrid={() => setShowGrid((value) => !value)}
      showLabels={showLabels}
      onToggleLabels={() => setShowLabels((value) => !value)}
      onLeave={() => {
        setCode(null);
        window.history.replaceState(null, "", "/");
      }}
    />
  );
}

interface SessionScreenProps {
  readonly code: string;
  readonly identity: Identity;
  readonly onIdentityChange: (identity: Identity) => void;
  readonly showGrid: boolean;
  readonly onToggleGrid: () => void;
  readonly showLabels: boolean;
  readonly onToggleLabels: () => void;
  readonly onLeave: () => void;
}

function SessionScreen({
  code,
  identity,
  onIdentityChange,
  showGrid,
  onToggleGrid,
  showLabels,
  onToggleLabels,
  onLeave,
}: SessionScreenProps): React.ReactElement {
  // `DEFAULT_MAP_ID` only ever seeds a brand-new session (see
  // `initialiseSession`) — once the session exists, `session.meta.mapId` is
  // the one true answer, so every peer stays on the same theatre as the
  // orders being issued against it.
  const session = useSession(code, identity, {
    mapId: DEFAULT_MAP_ID,
    websocketUrl: WEBSOCKET_URL,
  });

  const [cursorPlace, setCursorPlace] = useState<string | null>(null);
  const [pickedPlace, setPickedPlace] = useState<string | null>(null);
  const [placingMarker, setPlacingMarker] = useState<PendingMarker | null>(null);
  const [drawingRoute, setDrawingRoute] = useState<GameCoord[]>([]);
  const isDrawingRoute = drawingRoute.length > 0;
  const [contextMenu, setContextMenu] = useState<{
    readonly screenX: number;
    readonly screenY: number;
    readonly at: GameCoord;
    readonly place: string;
  } | null>(null);
  const mapApiRef = useRef<MapApi | null>(null);

  const map = getMapOrDefault(session.meta?.mapId ?? DEFAULT_MAP_ID);

  /**
   * Active taskings, in the shape the map wants. Cancelled and completed
   * orders are left off: the map shows what is happening now, not a history.
   */
  const orderMarkers = useMemo<OrderMarker[]>(
    () =>
      session.orders
        .filter((order) => order.status === "active")
        .map((order) => {
          const squad = session.squads.find((entry) => entry.id === order.squadId);
          return {
            id: order.id,
            squadId: order.squadId,
            squadTag: squad
              ? (session.squadTags.get(squad.id) ?? squadTag(squad.name || squad.id))
              : "?",
            kind: order.kind,
            at: order.at,
            place: order.place,
          };
        }),
    [session.orders, session.squads, session.squadTags],
  );
  // The session's shared faction once it exists, falling back to the join
  // form's choice before the first sync — never the header switch below,
  // which now only ever writes `session.meta`.
  const activeFaction = getFaction(session.meta?.faction ?? identity.faction);
  const { isCommander, setMap } = session;

  /** How many live tactical items sit on each map, so the switcher can show dots. */
  const mapDataCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const count = (mapId: string): void => {
      counts.set(mapId, (counts.get(mapId) ?? 0) + 1);
    };
    for (const m of session.allMarkers)
      count(m.mapId || (session.meta?.mapId ?? DEFAULT_MAP_ID));
    for (const r of session.allRoutes)
      count(r.mapId || (session.meta?.mapId ?? DEFAULT_MAP_ID));
    for (const o of session.allOrders) {
      if (o.status !== "cancelled" && o.status !== "complete") {
        count(o.mapId || (session.meta?.mapId ?? DEFAULT_MAP_ID));
      }
    }
    return counts;
  }, [session.allMarkers, session.allRoutes, session.allOrders, session.meta?.mapId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        if (target.isContentEditable) return;
      }

      switch (event.key.toLowerCase()) {
        case "g":
          onToggleGrid();
          break;
        case "l":
          onToggleLabels();
          break;
        case "escape":
          setPlacingMarker(null);
          setDrawingRoute([]);
          break;
        case "enter":
          if (drawingRoute.length >= 2) {
            session.putRoute(drawingRoute);
            setDrawingRoute([]);
          }
          break;
        case "1":
        case "2":
        case "3": {
          // The map is shared, so only the commander gets to move everyone
          // else's view out from under them with a keystroke too.
          if (!isCommander) break;
          const withImagery = MAPS.filter((entry) => entry.hasImagery);
          const target = withImagery[Number(event.key) - 1];
          if (target) setMap(target.id);
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onToggleGrid, onToggleLabels, isCommander, setMap, drawingRoute, session]);

  const handleSelectPoi = useCallback((poi: Poi) => {
    console.info("Selected", poi.label);
  }, []);

  /**
   * One map, two things a click might mean. A marker waiting to be placed
   * always wins — it is a deliberate, just-started action — otherwise a
   * commander's click picks an order target the way it always has. Anyone
   * else's click, with neither pending, is just panning.
   */
  const handleMapTap = useCallback(
    (place: string, at: { readonly x: number; readonly y: number }) => {
      if (placingMarker) {
        session.putMarker({
          kind: placingMarker.kind,
          at,
          ...(placingMarker.weaponId ? { weaponId: placingMarker.weaponId } : {}),
          ...(placingMarker.threat ? { threat: true } : {}),
        });
        setPlacingMarker(null);
        return;
      }
      if (isDrawingRoute) {
        setDrawingRoute((prev) => [...prev, at]);
        return;
      }
      if (session.isCommander) setPickedPlace(place);
    },
    [placingMarker, isDrawingRoute, session],
  );

  const confirmRoute = useCallback(() => {
    if (drawingRoute.length >= 2) {
      session.putRoute(drawingRoute);
    }
    setDrawingRoute([]);
  }, [drawingRoute, session]);

  const cancelRoute = useCallback(() => {
    setDrawingRoute([]);
  }, []);

  const handleContextMenuAt = useCallback(
    (screen: { x: number; y: number }, at: GameCoord, place: string) => {
      // A right-click is a fresh, deliberate choice — it should not have to
      // fight with a marker placement that is still pending from before.
      setPlacingMarker(null);
      setContextMenu({ screenX: screen.x, screenY: screen.y, at, place });
    },
    [],
  );

  /**
   * Leaving is a deliberate act, so it should behave like one: stand down and
   * hand off the squad immediately rather than making the team sit out the
   * disconnect grace period for someone who is not coming back this match.
   */
  const handleLeave = useCallback(() => {
    if (session.isCommander) session.resignCommand();
    if (session.mySquad) session.joinSquad(UNASSIGNED_SQUAD);
    onLeave();
  }, [session, onLeave]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border-subtle bg-surface px-3 py-2">
        <span
          className="text-sm font-bold tracking-wide"
          style={{ color: activeFaction.color }}
        >
          WARDOGS COMMAND
        </span>

        <SessionCode code={code} sync={session.sync} />

        <MapSwitcher
          current={map.id}
          onSelect={session.isCommander ? session.setMap : undefined}
          dataCounts={mapDataCounts}
          onNewMatch={session.isCommander ? session.newMatch : undefined}
        />

        <FactionSwitcher
          current={session.meta?.faction ?? identity.faction}
          onSelect={session.isCommander ? session.setFaction : undefined}
        />

        {/* `flex-wrap` on the header lets this group drop to its own row
            instead of clipping off the edge of the screen — this is the row
            that gets used on a tablet, propped up narrower than a desktop. */}
        <div className="ml-auto flex items-center gap-1">
          <PlaceSearch
            mapId={map.id}
            grid={gridConfigFor(map)}
            onGo={(at) => mapApiRef.current?.goTo(at)}
          />
          <MarkerPalette
            placing={placingMarker}
            onPick={setPlacingMarker}
            onCancel={() => setPlacingMarker(null)}
          />
          <Toggle label="Grid" active={showGrid} shortcut="G" onClick={onToggleGrid} />
          <Toggle label="Labels" active={showLabels} shortcut="L" onClick={onToggleLabels} />
          <ShortcutsHelp />
          <button
            type="button"
            onClick={handleLeave}
            className="ml-2 rounded px-2 py-1 text-xs text-text-muted transition-colors hover:text-danger"
          >
            Leave
          </button>
        </div>
      </header>

      <CommandBar session={session} myId={identity.id} myCallsign={identity.callsign} />

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="min-h-0 flex-1">
            <MapView
              map={map}
              showGrid={showGrid}
              showLabels={showLabels}
              onSelectPoi={handleSelectPoi}
              onCursorPlace={setCursorPlace}
              orders={orderMarkers}
              onRemoveOrder={session.isCommander ? session.cancel : undefined}
              markers={session.markers}
              myId={identity.id}
              canManageAllMarkers={session.isCommander}
              onRemoveMarker={session.deleteMarker}
              routes={session.routes}
              pendingRouteWaypoints={isDrawingRoute ? drawingRoute : undefined}
              onRemoveRoute={session.deleteRoute}
              placingIconUrl={placingMarker ? markerUrl(placingMarker.kind) : undefined}
              // A click means something whenever a marker is waiting to be
              // placed, or the commander is targeting an order; otherwise a
              // click is just a pan and the map should not treat it as a pick.
              onPickPlace={
                session.isCommander || placingMarker || isDrawingRoute
                  ? handleMapTap
                  : undefined
              }
              onContextMenuAt={handleContextMenuAt}
              onReady={(api) => {
                mapApiRef.current = api;
              }}
            />

            {contextMenu && (
              <MapContextMenu
                session={session}
                grid={gridConfigFor(map)}
                screenX={contextMenu.screenX}
                screenY={contextMenu.screenY}
                at={contextMenu.at}
                place={contextMenu.place}
                onStartRoute={(point) => {
                  setDrawingRoute([point]);
                  setContextMenu(null);
                }}
                onClose={() => setContextMenu(null)}
              />
            )}

            {isDrawingRoute && (
              <div className="pointer-events-auto absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 rounded border border-accent bg-surface-raised/95 px-4 py-2 shadow-lg backdrop-blur">
                <span className="text-xs font-medium text-accent">
                  Drawing route — click to add waypoints
                </span>
                <span className="numeric text-[10px] text-text-muted">
                  {drawingRoute.length} point{drawingRoute.length === 1 ? "" : "s"}
                </span>
                <button
                  type="button"
                  onClick={confirmRoute}
                  disabled={drawingRoute.length < 2}
                  className="rounded bg-accent px-3 py-1 text-xs font-semibold text-surface disabled:opacity-40"
                >
                  Done
                </button>
                <button
                  type="button"
                  onClick={cancelRoute}
                  className="rounded px-2 py-1 text-xs text-text-muted hover:text-danger"
                >
                  Cancel
                </button>
              </div>
            )}
          </main>

          <OrderPanel
            session={session}
            cursorPlace={cursorPlace}
            pickedPlace={pickedPlace}
            onClearPick={() => setPickedPlace(null)}
          />
        </div>

        <SquadRoster
          session={session}
          myId={identity.id}
          myRole={identity.role}
          myCallsign={identity.callsign}
          onChangeRole={(role) => onIdentityChange({ ...identity, role })}
          onChangeCallsign={(callsign) => onIdentityChange({ ...identity, callsign })}
        />
      </div>

      <Footer />
    </div>
  );
}

function SessionCode({
  code,
  sync,
}: {
  readonly code: string;
  readonly sync: ReturnType<typeof useSession>["sync"];
}): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/join/${code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be refused; the code is on screen regardless.
    }
  };

  const tone =
    sync.status === "connected"
      ? "var(--color-success)"
      : sync.status === "error"
        ? "var(--color-danger)"
        : "var(--color-text-muted)";

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title="Copy the invite link"
      className="flex items-center gap-2 rounded border border-border-subtle px-2 py-1 transition-colors hover:border-border-strong"
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tone }} />
      <span className="numeric text-xs tracking-widest text-text-primary">
        {formatRoomCode(code)}
      </span>
      <span className="text-[10px] text-text-muted">{copied ? "copied" : "copy"}</span>
    </button>
  );
}

function MapSwitcher({
  current,
  onSelect,
  dataCounts,
  onNewMatch,
}: {
  readonly current: string;
  /** Omitted for anyone but the commander — the map is shared, not personal. */
  readonly onSelect: ((id: string) => void) | undefined;
  /** Live tactical items per map id, for indicator dots on inactive maps. */
  readonly dataCounts: ReadonlyMap<string, number>;
  /** Commander-only: wipe the current map's tactical layer for a new match. */
  readonly onNewMatch: (() => void) | undefined;
}): React.ReactElement {
  const [confirmingNewMatch, setConfirmingNewMatch] = useState(false);

  return (
    <div className="flex items-center gap-1 border-l border-border-subtle pl-3">
      {MAPS.filter((entry) => entry.hasImagery).map((entry) => {
        const region = REGIONS.find((item) => item.id === entry.region);
        const label = `${region?.label ?? ""} — ${entry.label}`;
        const count = dataCounts.get(entry.id) ?? 0;
        const isActive = entry.id === current;
        return (
          <button
            key={entry.id}
            type="button"
            disabled={!onSelect}
            onClick={onSelect ? () => onSelect(entry.id) : undefined}
            title={
              onSelect
                ? `${label}${!isActive && count > 0 ? ` (${count} items)` : ""}`
                : `${label} (only the commander can change the map)`
            }
            className={
              isActive
                ? "relative rounded border border-border-strong bg-surface-raised px-2.5 py-1 text-xs font-medium text-text-primary"
                : "relative rounded border border-transparent px-2.5 py-1 text-xs text-text-muted transition-colors hover:text-text-secondary disabled:cursor-default disabled:hover:text-text-muted"
            }
          >
            {entry.label}
            {!isActive && count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-accent" />
            )}
          </button>
        );
      })}

      {onNewMatch && (
        <>
          {confirmingNewMatch ? (
            <button
              type="button"
              onClick={() => {
                onNewMatch();
                setConfirmingNewMatch(false);
              }}
              onBlur={() => setConfirmingNewMatch(false)}
              autoFocus
              className="rounded bg-danger px-2 py-1 text-[10px] font-semibold text-surface"
            >
              Clear map?
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingNewMatch(true)}
              title="New match — clears orders, markers and routes on this map. Squads stay."
              className="rounded px-2 py-1 text-[10px] text-text-muted transition-colors hover:text-warning"
            >
              New match
            </button>
          )}
        </>
      )}
    </div>
  );
}

function FactionSwitcher({
  current,
  onSelect,
}: {
  readonly current: FactionId;
  /** Omitted for anyone but the commander — the whole team plays one side. */
  readonly onSelect: ((id: FactionId) => void) | undefined;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-1 border-l border-border-subtle pl-3">
      {FACTIONS.map((entry) => (
        <button
          key={entry.id}
          type="button"
          disabled={!onSelect}
          onClick={onSelect ? () => onSelect(entry.id) : undefined}
          title={onSelect ? entry.blurb : `${entry.blurb} (only the commander can change this)`}
          className="rounded px-2 py-1 text-xs font-semibold transition-opacity disabled:cursor-default"
          style={
            entry.id === current
              ? { backgroundColor: entry.color, color: entry.onColor }
              : { color: entry.color, opacity: 0.45 }
          }
        >
          {entry.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({
  label,
  active,
  shortcut,
  onClick,
}: {
  readonly label: string;
  readonly active: boolean;
  readonly shortcut: string;
  readonly onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} (${shortcut})`}
      className={
        active
          ? "rounded border border-border-strong bg-surface-raised px-2.5 py-1 text-xs text-text-primary"
          : "rounded border border-transparent px-2.5 py-1 text-xs text-text-muted hover:text-text-secondary"
      }
    >
      {label}
      <span className="ml-1.5 text-[10px] text-text-muted">{shortcut}</span>
    </button>
  );
}
